import React, { useState, useEffect, FormEvent, useRef } from 'react';
import { useProfile } from '../../contexts/ProfileContext';
import { UserProfileUpdateRequest, DisplayNameStatus } from '@openmirror/shared';
import Spinner from '../UI/Spinner';
import { XMarkIcon, UserCircleIcon, CheckCircleIcon, XCircleIcon, ClockIcon } from '../UI/Icons';

interface ProfileSettingsPanelProps {
  isOpen: boolean;
  onClose: () => void;
}

const ProfileSettingsPanel: React.FC<ProfileSettingsPanelProps> = ({ isOpen, onClose }: ProfileSettingsPanelProps) => {
  const {
    profile,
    isLoadingProfile,
    profileError,
    updateProfile,
    uploadProfilePictureFile,
    isUploadingPicture,
    uploadError
  } = useProfile();
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  const [formData, setFormData] = useState<UserProfileUpdateRequest>({
    displayName: '',
    // profilePictureUrl will be managed by the profile context after upload,
    // but we can use it for local preview if needed before the context updates.
    // For simplicity, we'll primarily rely on profile.profilePictureUrl for display.
    profilePictureUrl: '', 
    description: '',
    about: '',
    email: '',
    address: '',
    website: '',
  });
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saveSuccess, setSaveSuccess] = useState<string | null>(null);

  useEffect(() => {
    if (profile) {
      setFormData({
        displayName: profile.displayName || '',
        profilePictureUrl: profile.profilePictureUrl || '',
        description: profile.description || '',
        about: profile.about || '',
        email: profile.email || '',
        address: profile.address || '',
        website: profile.website || '',
      });
    }
  }, [profile]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const [localPreviewUrl, setLocalPreviewUrl] = useState<string | null>(null);
  const [uploadAttempted, setUploadAttempted] = useState(false);


  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setUploadAttempted(false); // Reset upload attempted state
      // Generate local preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setLocalPreviewUrl(reader.result as string);
      };
      reader.readAsDataURL(file);

      // Immediately attempt to upload the file
      setUploadAttempted(true); // Mark that an upload has been initiated by file selection
      const uploadResponse = await uploadProfilePictureFile(file);
      if (uploadResponse.success && uploadResponse.newProfilePictureUrl) {
        // Profile context will update `profile.profilePictureUrl`
        // which will trigger re-render with the new server URL.
        // Clear local preview if server URL is now available and matches.
        setLocalPreviewUrl(null);
        setSaveSuccess("Profile picture updated!"); // Use general save success for feedback
        setTimeout(() => setSaveSuccess(null), 3000);
      } else {
        // Error is handled by `uploadError` from context, displayed below.
        // Keep local preview so user sees what they tried to upload.
      }
      // Reset the file input so the same file can be selected again if needed after an error
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  const triggerFileInput = () => {
    fileInputRef.current?.click();
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setIsSaving(true);
    setSaveError(null);
    setSaveSuccess(null);
    const response = await updateProfile(formData);
    if (response.success) {
        setSaveSuccess("Profile updated successfully!");
        setTimeout(() => setSaveSuccess(null), 3000); 
    } else {
        setSaveError(response.message || "Failed to save profile.");
    }
    setIsSaving(false);
  };

  const getStatusIndicator = () => {
    if (!profile || !profile.displayNameStatus) return null;

    const status = profile.displayNameStatus;
    const statusText = status.charAt(0).toUpperCase() + status.slice(1);

    let icon = null;
    let textColor = 'text-gray-700';
    let bgColor = 'bg-gray-100';

    switch (status) {
      case DisplayNameStatus.APPROVED:
        icon = <CheckCircleIcon className="w-4 h-4 mr-1 text-green-600" />;
        textColor = 'text-green-700';
        bgColor = 'bg-green-100';
        break;
      case DisplayNameStatus.DISAPPROVED:
        icon = <XCircleIcon className="w-4 h-4 mr-1 text-red-600" />;
        textColor = 'text-red-700';
        bgColor = 'bg-red-100';
        break;
      case DisplayNameStatus.PENDING:
        icon = <ClockIcon className="w-4 h-4 mr-1 text-yellow-600" />;
        textColor = 'text-yellow-700';
        bgColor = 'bg-yellow-100';
        break;
    }

    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${bgColor} ${textColor}`}>
        {icon}
        {statusText}
      </span>
    );
  };


  if (!isOpen) return null;

  return (
    <>
      <div 
        className="fixed inset-0 bg-black bg-opacity-50 z-40 transition-opacity duration-300 ease-in-out"
        onClick={onClose}
        aria-hidden="true"
      ></div>

      {/* Centered Modal Panel */}
      <div
        className="fixed inset-0 z-50 flex items-center justify-center p-4"
        role="dialog"
        aria-modal="true"
        aria-labelledby="profile-settings-title"
      >
        <div className="bg-white rounded-lg shadow-xl w-full max-w-md max-h-[90vh] transform transition-all duration-300 ease-in-out scale-100 opacity-100">
          <div className="flex flex-col h-full max-h-[90vh]">
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-gray-200">
            <h2 id="profile-settings-title" className="text-lg font-semibold text-gray-800">Profile Settings</h2>
            <button 
              onClick={onClose} 
              className="text-gray-500 hover:text-gray-700 p-1 rounded-full hover:bg-gray-100"
              aria-label="Close profile settings"
            >
              <XMarkIcon className="w-6 h-6" />
            </button>
          </div>

          {/* Content */}
          <div className="flex-grow p-6 overflow-y-auto">
            {isLoadingProfile && !profile && <div className="flex justify-center items-center h-32"><Spinner /></div>}
            {profileError && <p className="text-red-500 bg-red-100 p-3 rounded-md mb-4">{profileError}</p>}
            
            {profile && (
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="flex flex-col items-center space-y-3">
                  <div className="relative">
                    {isUploadingPicture && (
                      <div className="absolute inset-0 flex items-center justify-center bg-white bg-opacity-75 rounded-full">
                        <Spinner />
                      </div>
                    )}
                    <img 
                      src={localPreviewUrl || profile.profilePictureUrl || undefined}
                      alt="Profile Preview" 
                      className={`w-24 h-24 rounded-full object-cover border border-gray-300 ${isUploadingPicture ? 'opacity-50' : ''}`}
                      onError={(e) => { // Fallback if src is invalid or image fails to load
                        const target = e.target as HTMLImageElement;
                        target.onerror = null; // Prevent infinite loop if fallback also fails
                        target.style.display = 'none'; // Hide broken image icon
                        // Show UserCircleIcon or similar as fallback directly here, or manage via state
                      }}
                    />
                     {!(localPreviewUrl || profile.profilePictureUrl) && !isUploadingPicture && (
                        <UserCircleIcon className="w-24 h-24 text-gray-300" />
                     )}
                  </div>
                  <input
                    type="file"
                    ref={fileInputRef}
                    onChange={handleFileChange}
                    accept="image/jpeg,image/png" // Be more specific with accepted types
                    className="hidden" 
                    aria-label="Upload profile picture"
                    disabled={isUploadingPicture}
                  />
                  <button
                    type="button"
                    onClick={triggerFileInput}
                    disabled={isUploadingPicture || isLoadingProfile}
                    className="px-4 py-2 text-sm font-medium text-green-700 bg-green-100 border border-green-300 rounded-md shadow-sm hover:bg-green-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:bg-gray-200 disabled:text-gray-500"
                  >
                    {isUploadingPicture ? 'Uploading...' : 'Change Picture'}
                  </button>
                  {uploadAttempted && uploadError && <p className="text-sm text-red-600 text-center">{uploadError}</p>}
                </div>

                <div>
                  <div className="flex justify-between items-center mb-1">
                    <label htmlFor="displayName" className="block text-sm font-medium text-gray-700">Display Name</label>
                    {getStatusIndicator()}
                  </div>
                  <input
                    type="text"
                    name="displayName"
                    id="displayName"
                    value={formData.displayName}
                    onChange={handleInputChange}
                    className="block w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm text-gray-900 focus:outline-none focus:ring-green-500 focus:border-green-500 sm:text-sm"
                  />
                </div>
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700">Email</label>
                  <input
                    type="email"
                    name="email"
                    id="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    className="mt-1 block w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm text-gray-900 focus:outline-none focus:ring-green-500 focus:border-green-500 sm:text-sm"
                  />
                </div>
                <div>
                  <label htmlFor="description" className="block text-sm font-medium text-gray-700">Short Description (Bio)</label>
                  <textarea
                    name="description"
                    id="description"
                    rows={2}
                    value={formData.description}
                    onChange={handleInputChange}
                    className="mt-1 block w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm text-gray-900 focus:outline-none focus:ring-green-500 focus:border-green-500 sm:text-sm"
                  ></textarea>
                </div>
                <div>
                  <label htmlFor="about" className="block text-sm font-medium text-gray-700">About Me</label>
                  <textarea
                    name="about"
                    id="about"
                    rows={4}
                    value={formData.about}
                    onChange={handleInputChange}
                    className="mt-1 block w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm text-gray-900 focus:outline-none focus:ring-green-500 focus:border-green-500 sm:text-sm"
                  ></textarea>
                </div>
                <div>
                  <label htmlFor="address" className="block text-sm font-medium text-gray-700">Address</label>
                  <input
                    type="text"
                    name="address"
                    id="address"
                    value={formData.address}
                    onChange={handleInputChange}
                    className="mt-1 block w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm text-gray-900 focus:outline-none focus:ring-green-500 focus:border-green-500 sm:text-sm"
                  />
                </div>
                <div>
                  <label htmlFor="website" className="block text-sm font-medium text-gray-700">Website URL</label>
                  <input
                    type="url"
                    name="website"
                    id="website"
                    value={formData.website}
                    onChange={handleInputChange}
                    className="mt-1 block w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm text-gray-900 focus:outline-none focus:ring-green-500 focus:border-green-500 sm:text-sm"
                  />
                </div>
                
                {saveError && <p className="text-sm text-red-600 bg-red-100 p-2 rounded">{saveError}</p>}
                {saveSuccess && <p className="text-sm text-green-600 bg-green-100 p-2 rounded">{saveSuccess}</p>}

                <div className="pt-2 flex justify-end space-x-3">
                  <button
                    type="button"
                    onClick={onClose}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={isSaving || isLoadingProfile}
                    className="px-4 py-2 text-sm font-medium text-white bg-green-600 border border-transparent rounded-md shadow-sm hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:bg-gray-400 flex items-center"
                  >
                    {isSaving ? <Spinner size="sm" color="border-white"/> : 'Save Changes'}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      </div>
    </div>
  </>
);
};

export default ProfileSettingsPanel;